import { useEffect } from 'react';

interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
}

export const useSEO = (seoData: SEOData) => {
  useEffect(() => {
    // Update document title
    if (seoData.title) {
      document.title = seoData.title;
    }

    // Helper function to update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (element) {
        element.content = content;
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.content = content;
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (element) {
        element.href = href;
      } else {
        element = document.createElement('link');
        element.rel = rel;
        element.href = href;
        document.head.appendChild(element);
      }
    };

    // Update meta tags
    if (seoData.description) {
      updateMetaTag('description', seoData.description);
    }

    if (seoData.keywords) {
      updateMetaTag('keywords', seoData.keywords);
    }

    // Update canonical URL
    if (seoData.canonical) {
      updateLinkTag('canonical', seoData.canonical);
    }

    // Update Open Graph tags
    if (seoData.ogTitle) {
      updateMetaTag('og:title', seoData.ogTitle, true);
    }

    if (seoData.ogDescription) {
      updateMetaTag('og:description', seoData.ogDescription, true);
    }

    if (seoData.ogImage) {
      updateMetaTag('og:image', seoData.ogImage, true);
    }

    if (seoData.ogUrl) {
      updateMetaTag('og:url', seoData.ogUrl, true);
    }

    // Update Twitter tags
    if (seoData.twitterTitle) {
      updateMetaTag('twitter:title', seoData.twitterTitle, true);
    }

    if (seoData.twitterDescription) {
      updateMetaTag('twitter:description', seoData.twitterDescription, true);
    }

    if (seoData.twitterImage) {
      updateMetaTag('twitter:image', seoData.twitterImage, true);
    }

    // Update structured data
    if (seoData.structuredData) {
      let scriptElement = document.querySelector('script[type="application/ld+json"][data-dynamic]') as HTMLScriptElement;
      
      if (scriptElement) {
        scriptElement.textContent = JSON.stringify(seoData.structuredData);
      } else {
        scriptElement = document.createElement('script');
        scriptElement.type = 'application/ld+json';
        scriptElement.setAttribute('data-dynamic', 'true');
        scriptElement.textContent = JSON.stringify(seoData.structuredData);
        document.head.appendChild(scriptElement);
      }
    }
  }, [seoData]);
};

// SEO data configurations for different pages
export const SEO_CONFIGS = {
  home: {
    title: 'PermitLab: AI-Powered Permitting Analytics & Benchmarking Tool',
    description: 'Compare your jurisdiction\'s permitting performance with peer cities. Get AI-powered insights, readiness assessments, and data-driven recommendations to streamline your permitting process.',
    keywords: 'permitting, government analytics, AI permitting, municipal benchmarking, permit processing, government efficiency, regulatory technology, govtech',
    canonical: 'https://permitlab.govstream.ai/',
    ogTitle: 'PermitLab: AI-Powered Permitting Analytics & Benchmarking Tool',
    ogDescription: 'Compare your jurisdiction\'s permitting performance with peer cities. Get AI-powered insights, readiness assessments, and data-driven recommendations to streamline your permitting process.',
    ogUrl: 'https://permitlab.govstream.ai/',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PermitLab",
      "description": "AI-powered permitting analytics and benchmarking tool for government jurisdictions",
      "url": "https://permitlab.govstream.ai",
      "author": {
        "@type": "Organization",
        "name": "Govstream.ai",
        "url": "https://govstream.ai"
      },
      "applicationCategory": "GovernmentApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "featureList": [
        "Permitting performance benchmarking",
        "AI-powered analytics",
        "Peer jurisdiction comparison",
        "Readiness assessment tools",
        "Data visualization dashboards"
      ]
    }
  },
  benchmarking: {
    title: 'Permit Benchmarking Tool - Compare Your Jurisdiction Performance | PermitLab',
    description: 'Benchmark your jurisdiction\'s permitting performance against peer cities. Analyze permits per capita, processing times, and housing growth metrics with interactive data visualizations.',
    keywords: 'permit benchmarking, jurisdiction comparison, permitting metrics, municipal performance, government data analysis, permitting efficiency',
    canonical: 'https://permitlab.govstream.ai/benchmarking',
    ogTitle: 'Permit Benchmarking Tool - Compare Your Jurisdiction Performance | PermitLab',
    ogDescription: 'Benchmark your jurisdiction\'s permitting performance against peer cities. Analyze permits per capita, processing times, and housing growth metrics with interactive data visualizations.',
    ogUrl: 'https://permitlab.govstream.ai/benchmarking',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "PermitLab Benchmarking Tool",
      "description": "Interactive tool for benchmarking jurisdiction permitting performance against peer cities",
      "url": "https://permitlab.govstream.ai/benchmarking",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "browserRequirements": "Modern web browser with JavaScript enabled",
      "permissions": "No special permissions required",
      "author": {
        "@type": "Organization",
        "name": "Govstream.ai",
        "url": "https://govstream.ai"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  },
  readinessAssessment: {
    title: 'AI Readiness Assessment for Permitting - Evaluate Your Organization | PermitLab',
    description: 'Assess your organization\'s readiness for AI-powered permitting solutions. Evaluate infrastructure, policy, leadership, and technical capabilities with our comprehensive assessment tool.',
    keywords: 'AI readiness assessment, permitting modernization, government digital transformation, AI implementation, permitting technology assessment',
    canonical: 'https://permitlab.govstream.ai/readiness/assessment',
    ogTitle: 'AI Readiness Assessment for Permitting - Evaluate Your Organization | PermitLab',
    ogDescription: 'Assess your organization\'s readiness for AI-powered permitting solutions. Evaluate infrastructure, policy, leadership, and technical capabilities with our comprehensive assessment tool.',
    ogUrl: 'https://permitlab.govstream.ai/readiness/assessment',
  },
  opportunityAssessment: {
    title: 'Permitting Opportunity Assessment - Identify Improvement Areas | PermitLab',
    description: 'Identify opportunities to improve your permitting process. Get data-driven recommendations and prioritized action items to enhance efficiency and citizen satisfaction.',
    keywords: 'permitting opportunities, process improvement, permitting optimization, government efficiency, permit streamlining',
    canonical: 'https://permitlab.govstream.ai/assessment',
    ogTitle: 'Permitting Opportunity Assessment - Identify Improvement Areas | PermitLab',
    ogDescription: 'Identify opportunities to improve your permitting process. Get data-driven recommendations and prioritized action items to enhance efficiency and citizen satisfaction.',
    ogUrl: 'https://permitlab.govstream.ai/assessment',
  },
  landing: {
    title: 'Welcome to PermitLab - Transform Your Permitting Process | PermitLab',
    description: 'Discover how PermitLab can help modernize your jurisdiction\'s permitting process with AI-powered analytics, benchmarking tools, and readiness assessments.',
    keywords: 'permitting modernization, government technology, AI permitting, permit process improvement, govtech solutions',
    canonical: 'https://permitlab.govstream.ai/landing',
    ogTitle: 'Welcome to PermitLab - Transform Your Permitting Process | PermitLab',
    ogDescription: 'Discover how PermitLab can help modernize your jurisdiction\'s permitting process with AI-powered analytics, benchmarking tools, and readiness assessments.',
    ogUrl: 'https://permitlab.govstream.ai/landing',
  },
} as const;
